// The deployment's brand (ADR 0018, ADR 0024, pc-i3a.6). The pack is chosen per
// deployment: the web image's entrypoint writes BRAND_PACK into index.html as
// <html data-brand> and links that pack's stylesheet. The app loads the same pack's
// manifest (and only that one), and the tenant's values from GET /api/tenant go on top.
import { selectBrandPack, type BrandPack, type TenantBrand } from '@hkdevs/design-system';
import type { Tenant } from '../api/queries/tenant';

/**
 * Each shipped pack's manifest, loaded on demand so a page load fetches only its own
 * (the LEADSPPLY manifest carries its logos). Keep in step with the design system's
 * shipped packs; a unit test checks.
 */
export const PACK_LOADERS: Record<string, () => Promise<BrandPack>> = {
  default: () => import('@hkdevs/design-system/themes/default').then((module) => module.pack),
  leadspply: () => import('@hkdevs/design-system/themes/leadspply').then((module) => module.pack),
};

/**
 * The configured pack, from <html data-brand>. Throws, naming the known packs, when
 * the attribute is missing or names no pack: the app must not start as another brand.
 */
export function loadDeploymentPack(
  id: string | undefined = document.documentElement.dataset.brand,
): Promise<BrandPack> {
  return selectBrandPack(id, PACK_LOADERS)();
}

/**
 * The tenant's brand values. `brandPack` isn't among them: the API reads the same
 * BRAND_PACK, but the pack the page loaded is the one in the HTML shell.
 */
export function tenantBrand(tenant: Tenant): TenantBrand {
  return {
    brandName: tenant.brandName,
    productName: tenant.productName,
    logo: tenant.logo,
    accent: tenant.brandAccent,
    density: tenant.density,
    secureHost: tenant.secureHost,
    helpLinks: tenant.helpLinks,
  };
}
